// Zod schemas mirroring every public.* base table from sub-plan 02 +
// table-add migrations 00006/00009. Field shapes match the SQL columns
// exactly — nullable in SQL → `.nullable()`, defaults are not encoded
// (Zod schemas describe the wire shape returned by PostgREST, where
// every column is present after a SELECT).
//
// Re-using validators across schemas:
//   uuid     — RFC 4122 UUID string
//   isoDate  — ISO-8601 datetime string (matches Postgres timestamptz JSON)
//
// Total `export const` table schemas in this file: 24
//   1.  UserProfile               13. RewardPointsLedgerEntry
//   2.  CustomerProfile           14. RewardLevel
//   3.  EngineerProfile           15. Ticket
//   4.  EngineerQualification     16. TicketQuestionTemplate
//   5.  Address                   17. Notification
//   6.  PaymentMethod             18. PushToken
//   7.  JobCategory               19. AuthRateLimit
//   8.  JobDescriptionTemplate    20. AdminPermissions
//   9.  Job                       21. UserDeletionRequest      (00006)
//   10. JobStatusHistory          22. EngineerStatusHistory    (00009)
//   11. JobPhoto                  23. EngineerLocationPing
//   12. Rating                    24. AdminUserMe              (composite — admin-users/me)

import { z } from "zod";
import {
  userRole,
  jobStatus,
  ticketStatus,
  ticketKind,
  engineerApproval,
  engineerQualLevel,
  customerLevel,
  paymentKind,
  notificationKind,
  jobPhotoKind,
  jobExtraStatus,
  withdrawalStatus,
  engineerQualificationKind,
  ratingKind,
  adminRoleLabel,
} from "./enums";

const uuid = z.string().uuid();
const isoDate = z.string().datetime();

// --- 1. user_profiles ---------------------------------------------------
export const UserProfile = z.object({
  id: uuid,
  role: z.enum(userRole),
  display_name: z.string().nullable(),
  // Migration 00011 splits display_name into first_name + last_name.
  // display_name remains the canonical wire field (auto-derived via DB
  // trigger when first/last are set), so existing readers keep working.
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  // Migration 00011 also adds username as a separate profile-level
  // identifier — distinct from email (Supabase Auth login). 3-50 chars,
  // case-insensitive unique when set; nullable so legacy rows aren't
  // forced to backfill.
  username: z.string().nullable(),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  avatar_url: z.string().url().nullable(),
  is_active: z.boolean(),
  created_at: isoDate,
  updated_at: isoDate,
});
export type UserProfile = z.infer<typeof UserProfile>;

// --- 2. customer_profiles -----------------------------------------------
export const CustomerProfile = z.object({
  user_id: uuid,
  level: z.enum(customerLevel),
  reward_points: z.number().int(),
  default_address_id: uuid.nullable(),
  created_at: isoDate,
});
export type CustomerProfile = z.infer<typeof CustomerProfile>;

// --- 3. engineer_profiles -----------------------------------------------
export const EngineerProfile = z.object({
  user_id: uuid,
  qualification_level: z.enum(engineerQualLevel),
  approval_status: z.enum(engineerApproval),
  rating_avg: z.number(),
  completed_jobs: z.number().int(),
  work_experience_years: z.number().int(),
  bio: z.string().nullable(),
  group_name: z.string(),
  is_active: z.boolean(),
  // Migration 00013 — multi-select of HK 18 districts. `[]` = unset.
  service_areas: z.array(z.string()).default([]),
  // 00007 adds rejection_reason (sub-plan 07). Present in the wire shape
  // for any project that has applied 00007, absent otherwise — modelled
  // as nullable + optional so both states are accepted.
  rejection_reason: z.string().nullable().optional(),
  created_at: isoDate,
});
export type EngineerProfile = z.infer<typeof EngineerProfile>;

// --- 4. engineer_qualifications -----------------------------------------
export const EngineerQualification = z.object({
  id: uuid,
  engineer_id: uuid,
  kind: z.enum(engineerQualificationKind),
  storage_path: z.string(),
  reviewed_by: uuid.nullable(),
  reviewed_at: isoDate.nullable(),
  expires_at: isoDate.nullable(),
  uploaded_at: isoDate,
  // Migration 00013 — free-form label, used only for kind=qualification
  // ("HKIE Cert", "Trade License", etc.). NULL on kind=photo_id rows.
  label: z.string().max(120).nullable().optional(),
});
export type EngineerQualification = z.infer<typeof EngineerQualification>;

// HK 18 districts — used by engineer signup multi-select and the customer/
// engineer matching gate. Wire format is the canonical district name; the
// label-to-code mapping (if any) lives in apps/mobile so the wire stays
// human-readable for admin debugging.
export const hkDistricts = [
  "Central and Western",
  "Wan Chai",
  "Eastern",
  "Southern",
  "Yau Tsim Mong",
  "Sham Shui Po",
  "Kowloon City",
  "Wong Tai Sin",
  "Kwun Tong",
  "Kwai Tsing",
  "Tsuen Wan",
  "Tuen Mun",
  "Yuen Long",
  "North",
  "Tai Po",
  "Sha Tin",
  "Sai Kung",
  "Islands",
] as const;
export type HkDistrict = (typeof hkDistricts)[number];

// --- 5. addresses -------------------------------------------------------
export const Address = z.object({
  id: uuid,
  user_id: uuid,
  label: z.string().nullable(),
  street: z.string(),
  apt: z.string().nullable(),
  district: z.string().nullable(),
  building: z.string().nullable(),
  region: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  is_default: z.boolean(),
  created_at: isoDate,
});
export type Address = z.infer<typeof Address>;

// --- 6. payment_methods -------------------------------------------------
export const PaymentMethod = z.object({
  id: uuid,
  user_id: uuid,
  kind: z.enum(paymentKind),
  label: z.string().nullable(),
  card_last4: z.string().nullable(),
  card_brand: z.string().nullable(),
  // `card_plain` is a phase-1-ONLY column (sub-plan 02 line 228). Migration
  // 00011 drops it before any real PSP integration. Marked optional so the
  // schema keeps validating once the column is gone.
  card_plain: z.string().nullable().optional(),
  is_default: z.boolean(),
  created_at: isoDate,
});
export type PaymentMethod = z.infer<typeof PaymentMethod>;

// --- 7. job_categories --------------------------------------------------
// price_tier_id added by migration 00019 (P1 — pricing foundation). Nullable
// because the migration backfills existing rows but new categories created
// before an admin assigns a tier would fall back to the BASIC seed via the
// freeze_job_creation RPC.
export const JobCategory = z.object({
  id: uuid,
  name: z.string(),
  description: z.string().nullable(),
  is_active: z.boolean(),
  price_tier_id: uuid.nullable().optional(),
});
export type JobCategory = z.infer<typeof JobCategory>;

// Customer-facing GET /v1/customer/job-categories returns each category with
// the embedded tier shape so the new-job pill can show "Estimated cost: HK$X"
// without a second round-trip. price_tier may be null only for orphan
// categories (the BASIC fallback runs at job-create time, not at list time).
export const JobCategoryWithTier = JobCategory.extend({
  price_tier: z
    .object({
      id: uuid,
      code: z.string(),
      label: z.string(),
      base_price_cents: z.number().int(),
      est_minutes: z.number().int(),
    })
    .nullable()
    .optional(),
});
export type JobCategoryWithTier = z.infer<typeof JobCategoryWithTier>;

// --- 8. job_description_templates ---------------------------------------
export const JobDescriptionTemplate = z.object({
  id: uuid,
  category_id: uuid.nullable(),
  title: z.string(),
  body: z.string(),
});
export type JobDescriptionTemplate = z.infer<typeof JobDescriptionTemplate>;

// --- 9. jobs ------------------------------------------------------------
// base_price_cents + price_tier_id added by migration 00019 (P1 — frozen at
// job creation, see freeze_job_creation RPC). Nullable in the wire shape
// because the migration intentionally leaves the DB column NOT-NULL-less for
// deploy-window safety; the API layer guarantees it on every new row.
//
// customer_confirmed_at: column has existed since 00006 but was missing from
// this Zod schema. Formalised here for P4 — drives the
// pending_customer_confirmation → completed transition.
export const Job = z.object({
  id: uuid,
  customer_id: uuid,
  engineer_id: uuid.nullable(),
  preferred_engineer_id: uuid.nullable(),
  address_id: uuid.nullable(),
  category_id: uuid.nullable(),
  title: z.string(),
  description: z.string().nullable(),
  required_qualification: z.enum(engineerQualLevel),
  status: z.enum(jobStatus),
  service_started_at: isoDate.nullable(),
  service_ended_at: isoDate.nullable(),
  price_amount: z.number().nullable(),
  currency: z.string(),
  remarks: z.string().nullable(),
  base_price_cents: z.number().int().nullable().optional(),
  price_tier_id: uuid.nullable().optional(),
  customer_confirmed_at: isoDate.nullable().optional(),
  // P4 (migration 00020b) — completion + holds.
  accepted_at: isoDate.nullable().optional(),
  engineer_completed_at: isoDate.nullable().optional(),
  total_amount_cents: z.number().int().nullable().optional(),
  // P3 (migration 00021) — running sum of approved extras. Pre-added by P4
  // 00020b with default 0 so settle/cancel logic can read it before P3 ships.
  extras_total_cents: z.number().int().optional(),
  created_at: isoDate,
  updated_at: isoDate,
});
export type Job = z.infer<typeof Job>;

// --- 10. job_status_history --------------------------------------------
export const JobStatusHistory = z.object({
  id: uuid,
  job_id: uuid,
  from_status: z.enum(jobStatus).nullable(),
  to_status: z.enum(jobStatus),
  changed_by: uuid.nullable(),
  note: z.string().nullable(),
  changed_at: isoDate,
});
export type JobStatusHistory = z.infer<typeof JobStatusHistory>;

// --- 11. job_photos -----------------------------------------------------
export const JobPhoto = z.object({
  id: uuid,
  job_id: uuid,
  uploader_id: uuid.nullable(),
  kind: z.enum(jobPhotoKind),
  storage_path: z.string(),
  created_at: isoDate,
});
export type JobPhoto = z.infer<typeof JobPhoto>;

// --- 12. ratings --------------------------------------------------------
// Sub-rating columns are NULL for engineer→customer rows; the
// `ratings_subratings_match_kind` CHECK in 00001 enforces consistency.
export const Rating = z.object({
  id: uuid,
  job_id: uuid,
  rater_id: uuid,
  ratee_id: uuid,
  kind: z.enum(ratingKind),
  response_speed: z.number().nullable(),
  service_attitude: z.number().nullable(),
  engineer_rating: z.number().nullable(),
  overall: z.number(),
  review: z.string().nullable(),
  created_at: isoDate,
});
export type Rating = z.infer<typeof Rating>;

// --- 13. reward_points_ledger ------------------------------------------
export const RewardPointsLedgerEntry = z.object({
  id: uuid,
  customer_id: uuid,
  job_id: uuid.nullable(),
  delta: z.number().int(),
  reason: z.string(),
  expires_at: isoDate.nullable(),
  created_at: isoDate,
});
export type RewardPointsLedgerEntry = z.infer<typeof RewardPointsLedgerEntry>;

// --- 14. reward_levels --------------------------------------------------
export const RewardLevel = z.object({
  name: z.enum(customerLevel),
  min_points: z.number().int(),
  discount_percent: z.number(),
  valid_months: z.number().int(),
});
export type RewardLevel = z.infer<typeof RewardLevel>;

// --- 15. tickets --------------------------------------------------------
// ticket_kind added by P4 migration 00020b (logically owned by P6 but the
// autoConfirmJobs cron filters on it). related_job_id added by P6 migration
// 00023. Both optional for forward-compat with rows from earlier migrations.
export const Ticket = z.object({
  id: uuid,
  opener_id: uuid,
  subject: z.string(),
  body: z.string().nullable(),
  rating: z.number().int().min(1).max(5).nullable(),
  status: z.enum(ticketStatus),
  assigned_to: uuid.nullable(),
  resolved_at: isoDate.nullable(),
  ticket_kind: z.enum(ticketKind).optional(),
  related_job_id: uuid.nullable().optional(),
  created_at: isoDate,
  updated_at: isoDate,
});
export type Ticket = z.infer<typeof Ticket>;

// --- 16. ticket_question_templates -------------------------------------
export const TicketQuestionTemplate = z.object({
  id: uuid,
  category: z.string().nullable(),
  question: z.string(),
  order_idx: z.number().int(),
});
export type TicketQuestionTemplate = z.infer<typeof TicketQuestionTemplate>;

// --- 17. notifications --------------------------------------------------
export const Notification = z.object({
  id: uuid,
  recipient_id: uuid,
  kind: z.enum(notificationKind),
  title: z.string(),
  body: z.string().nullable(),
  data: z.record(z.unknown()).nullable(),
  read_at: isoDate.nullable(),
  delivered_at: isoDate.nullable(),
  created_at: isoDate,
});
export type Notification = z.infer<typeof Notification>;

// --- 18. push_tokens ----------------------------------------------------
export const PushToken = z.object({
  id: uuid,
  user_id: uuid,
  expo_token: z.string(),
  platform: z.enum(["ios", "android"]).nullable(),
  last_seen_at: isoDate,
});
export type PushToken = z.infer<typeof PushToken>;

// --- 19. auth_rate_limit ------------------------------------------------
export const AuthRateLimit = z.object({
  id: uuid,
  key: z.string(),
  bucket_window: isoDate,
  attempts: z.number().int(),
  locked_until: isoDate.nullable(),
});
export type AuthRateLimit = z.infer<typeof AuthRateLimit>;

// --- 20. admin_permissions ---------------------------------------------
// 5 original permission booleans are required (NOT partial); admin UI relies
// on every flag being present in the response. Migration 00019 (P1) adds
// can_manage_pricing — declared optional so historic rows still validate
// before the migration applies. `role_label` is the same enum used by
// AdminPermissionsPartial in api/admin.ts.
export const AdminPermissions = z.object({
  admin_id: uuid,
  can_manage_admins: z.boolean(),
  can_manage_app_users: z.boolean(),
  can_manage_jobs: z.boolean(),
  can_send_push: z.boolean(),
  can_view_reports: z.boolean(),
  can_manage_pricing: z.boolean().optional(),
  can_manage_withdrawals: z.boolean().optional(),
  role_label: z.enum(adminRoleLabel),
  // Migration 00012 adds role_id FK to admin_roles. Nullable while legacy
  // rows still rely on the per-user booleans above.
  role_id: uuid.nullable(),
  created_at: isoDate,
  updated_at: isoDate,
});
export type AdminPermissions = z.infer<typeof AdminPermissions>;

// --- 24. admin_roles (00012) -------------------------------------------
// Role-based permission set. The 3 system roles (Administrator | Event user
// | Normal User) are seeded by 00012 and protected from rename/delete by
// `is_system = true`. can_manage_pricing added by migration 00019 (P1) —
// optional for historic rows.
export const AdminRole = z.object({
  id: uuid,
  name: z.string().min(2).max(80),
  description: z.string().nullable(),
  can_manage_admins: z.boolean(),
  can_manage_app_users: z.boolean(),
  can_manage_jobs: z.boolean(),
  can_send_push: z.boolean(),
  can_view_reports: z.boolean(),
  can_manage_pricing: z.boolean().optional(),
  can_manage_withdrawals: z.boolean().optional(),
  is_system: z.boolean(),
  created_at: isoDate,
  updated_at: isoDate,
});
export type AdminRole = z.infer<typeof AdminRole>;

// --- 25. price_tiers (00019) -------------------------------------------
// One row per tier (BASIC / STANDARD / EXPERT, etc.). base_price_cents is
// the only billing-affecting field; est_minutes is guidance only.
export const PriceTier = z.object({
  id: uuid,
  code: z.string().regex(/^[A-Z_]{2,20}$/),
  label: z.string(),
  description: z.string().nullable(),
  base_price_cents: z.number().int().min(1000).max(1000000),
  est_minutes: z.number().int().min(5).max(600),
  sort_order: z.number().int(),
  is_active: z.boolean(),
  created_at: isoDate,
  updated_at: isoDate,
});
export type PriceTier = z.infer<typeof PriceTier>;

// Customer-facing trim of PriceTier — drops admin-only fields (is_active,
// sort_order, timestamps). Used by GET /v1/customer/price-tiers and the
// embedded price_tier on JobCategoryWithTier.
export const PublicPriceTier = PriceTier.pick({
  id: true,
  code: true,
  label: true,
  description: true,
  base_price_cents: true,
  est_minutes: true,
});
export type PublicPriceTier = z.infer<typeof PublicPriceTier>;

// --- 27. withdrawal_requests (00022b — P5) -----------------------------
// User-initiated bank-transfer requests. Admin processes via the queue page;
// 48h SLA disclosed in the in-app TOS for App Store / Play approval.
export const WithdrawalRequest = z.object({
  id: uuid,
  user_id: uuid,
  amount_cents: z.number().int().min(1),
  fee_cents: z.number().int().min(0),
  currency: z.string(),
  status: z.enum(withdrawalStatus),
  destination_type: z.literal("bank_transfer"),
  // Encrypted at rest with PAYMENT_CARD_ENCRYPTION_KEY (admin-only field —
  // GET /v1/admin/withdrawals/:id decrypts; everything else uses
  // destination_summary for display).
  destination_details_enc: z.string(),
  destination_summary: z.string(),
  wallet_transaction_id: uuid.nullable().optional(),
  processed_at: isoDate.nullable().optional(),
  processed_by: uuid.nullable().optional(),
  admin_notes: z.string().nullable().optional(),
  rejection_reason: z.string().nullable().optional(),
  requested_at: isoDate,
  updated_at: isoDate,
  request_ip: z.string().nullable().optional(),
  request_user_agent: z.string().nullable().optional(),
});
export type WithdrawalRequest = z.infer<typeof WithdrawalRequest>;

// Trim of WithdrawalRequest for the customer-facing list endpoint. Drops
// the encrypted destination details, the admin processed_by field, the
// fraud-trail IP/user_agent.
export const WithdrawalSummary = WithdrawalRequest.pick({
  id: true,
  amount_cents: true,
  fee_cents: true,
  status: true,
  destination_summary: true,
  requested_at: true,
  processed_at: true,
  rejection_reason: true,
});
export type WithdrawalSummary = z.infer<typeof WithdrawalSummary>;

// --- 26. job_extras (00021 — P3) ---------------------------------------
// One row per extra item proposed by an engineer mid-job. Customer approves
// or rejects each one; approved rows roll into jobs.extras_total_cents via
// the recalc_job_extras_total trigger.
export const JobExtra = z.object({
  id: uuid,
  job_id: uuid,
  proposed_by: uuid,
  label: z.string(),
  amount_cents: z.number().int().min(1).max(500_000),
  photo_storage_path: z.string().nullable().optional(),
  status: z.enum(jobExtraStatus),
  customer_responded_at: isoDate.nullable().optional(),
  rejection_reason: z.string().nullable().optional(),
  proposed_at: isoDate,
  updated_at: isoDate,
});
export type JobExtra = z.infer<typeof JobExtra>;

// --- 21. user_deletion_requests (00006) --------------------------------
// Reconciled to match migration 00006: user_id is nullable (set null on auth
// user delete); user_id_snapshot/phone_snapshot/email_snapshot carry forensic
// copies that survive hard-deletion; hard_deleted_at stamped by sub-plan 08
// cron after auth.admin.deleteUser succeeds.
export const UserDeletionRequest = z.object({
  id: uuid,
  user_id: uuid.nullable(),
  user_id_snapshot: uuid,
  phone_snapshot: z.string().nullable(),
  email_snapshot: z.string().nullable(),
  requested_at: isoDate,
  hard_deleted_at: isoDate.nullable(),
});
export type UserDeletionRequest = z.infer<typeof UserDeletionRequest>;

// --- 22. engineer_status_history (00009) -------------------------------
export const EngineerStatusHistory = z.object({
  id: uuid,
  engineer_id: uuid,
  from_status: z.enum(engineerApproval).nullable(),
  to_status: z.enum(engineerApproval),
  changed_by: uuid.nullable(),
  note: z.string().nullable(),
  changed_at: isoDate,
});
export type EngineerStatusHistory = z.infer<typeof EngineerStatusHistory>;

// --- 23. engineer_location_pings ---------------------------------------
export const EngineerLocationPing = z.object({
  id: z.number().int(), // bigserial — narrows to integer in JSON
  job_id: uuid,
  engineer_id: uuid,
  lat: z.number(),
  lng: z.number(),
  accuracy_m: z.number().nullable(),
  pinged_at: isoDate,
});
export type EngineerLocationPing = z.infer<typeof EngineerLocationPing>;

// --- 24. admin-users/me composite response ------------------------------
// Response shape of `GET /admin-users/me` (sub-plan 07 line 549). Composite
// of UserProfile + AdminPermissions; the permissions row is nullable for
// admin users that haven't had their permissions flag-set yet.
export const AdminUserMe = z.object({
  profile: UserProfile,
  admin_permissions: AdminPermissions.nullable(),
});
export type AdminUserMe = z.infer<typeof AdminUserMe>;
